/**
 * React Query hooks for project data management
 *
 * Provides hooks for fetching and mutating project data with
 * automatic cache invalidation and optimistic updates.
 */

import { useQuery, useMutation, useQueryClient, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import { projectService } from '../services/api';
import {
  Project,
  ProjectCreate,
  ProjectUpdate,
  ProjectListResponse,
  ProjectFilters,
} from '../types/project';

const PROJECTS_QUERY_KEY = 'projects';

/**
 * Hook to fetch list of projects with filters
 */
export function useProjects(filters?: ProjectFilters): UseQueryResult<ProjectListResponse, Error> {
  return useQuery({
    queryKey: [PROJECTS_QUERY_KEY, filters],
    queryFn: () => projectService.list(filters),
    // Keep data fresh for 1 minute
    staleTime: 60000,
  });
}

/**
 * Hook to fetch a single project by ID
 */
export function useProject(id: string | undefined): UseQueryResult<Project, Error> {
  return useQuery({
    queryKey: [PROJECTS_QUERY_KEY, id],
    queryFn: () => projectService.get(id!),
    enabled: !!id, // Only run query if id is provided
    staleTime: 60000,
  });
}

/**
 * Hook to create a new project
 */
export function useCreateProject(): UseMutationResult<Project, Error, ProjectCreate> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ProjectCreate) => projectService.create(data),
    onSuccess: () => {
      // Invalidate and refetch projects list
      queryClient.invalidateQueries({ queryKey: [PROJECTS_QUERY_KEY] });
    },
  });
}

/**
 * Hook to update an existing project
 */
export function useUpdateProject(): UseMutationResult<Project, Error, { id: string; data: ProjectUpdate }> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ProjectUpdate }) =>
      projectService.update(id, data),
    onSuccess: (updatedProject) => {
      // Invalidate projects list
      queryClient.invalidateQueries({ queryKey: [PROJECTS_QUERY_KEY] });

      // Update the cache for the specific project
      queryClient.setQueryData(
        [PROJECTS_QUERY_KEY, updatedProject.id],
        updatedProject
      );
    },
  });
}

/**
 * Hook to delete a project
 */
export function useDeleteProject(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => projectService.delete(id),
    onSuccess: () => {
      // Invalidate and refetch projects list
      queryClient.invalidateQueries({ queryKey: [PROJECTS_QUERY_KEY] });
    },
  });
}
